import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { environment } from 'src/environments/environment';
import { Observable, from, switchMap, tap, catchError, throwError, map } from 'rxjs';
import { SmartAssignProtocolBulkRequest, SmartAssignBulkResponse } from 'src/app/models/agenda.model';
import {
    AgendaDetailsResponse,
    ProtocolOrderResponse,
    ProtocolOrderDetailsResponse,
    CenterInfo,
    CurrentUserResponse
} from '../interfaces/work-orders.interface';
import {
    ProtocolOrderDto as ProtocolOrderFormDto,
    ProtocolType,
    PROTOCOL_TYPE_TO_QUERY_PARAM
} from 'src/app/core/interfaces/protocol-order.interface';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { NewOrderType } from 'src/app/analytics/models/analytics-event';

export interface WorkOrderChecklistItem {
    id: string;
    title: string;
    titleKey?: string;
    nameKey?: string;
    estimatedTimeMin: number;
    warning: string | null;
    warningKey?: string;
    isRequired?: boolean;
    description: string[];
    descriptionKeys?: string[];
    videoUrl: string;
    imageUrl: string;
    status: 'pending' | 'completed' | 'not-applicable';
    photos?: string[];
}

export interface WorkOrderPhase {
    id: string;
    title: string;
    titleKey?: string;
    description: string;
    descriptionKey?: string;
    nameKey?: string;
    checklist: WorkOrderChecklistItem[];
}

export interface WorkOrder {
    title: string;
    phases: WorkOrderPhase[];
}

export interface ProtocolOrderDto {
    id: string; // UUID
    externalId?: string;
    agendaId?: number;
    serviceType?: string;
    clientFullName?: string;
    vehicleBrand?: string;
    vehicleModel?: string;
    licensePlate?: string;
    scheduledDate?: string;
    status?: string;
}

export interface SearchDto {
    clients?: ClientInfo[];
    vehicles?: VehicleInfo[];
}

export interface ClientInfo {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    documentId: string;
}

export interface VehicleInfo {
    id: number;
    brand: string;
    model: string;
    licensePlate: string;
    vin: string;
    year: number;
    clientId: number;
}

export interface ProtocolOrderReport {
    id?: string;
    externalId?: string;
    agendaId?: number;
    clientFullName?: string;
    vehicleInfo?: string;
    protocolType?: string;
    createdDate?: string;
    completedDate?: string;
    status?: string;
    sections?: ProtocolSection[];
}

export interface ProtocolSection {
    id: string;
    title: string;
    description: string;
    steps: ProtocolStep[];
    properties: ProtocolProperty[];
}

export interface ProtocolStep {
    id: string;
    title: string;
    description: string;
    completed: boolean;
}

export interface ProtocolProperty {
    id: string;
    name: string;
    value: any;
    type: 'text' | 'number' | 'boolean' | 'image' | 'signature';
}

export interface PropertyPictureUploadRequest {
    PropertiesId: string[]; // Array de UUIDs
    PicturesId: string[];
    ExternalId: string;
    PropertiesPicture: File[];
}

export interface PropertySignatureUploadRequest {
    PropertiesId: string[]; // Array de UUIDs
    SignaturesId: string[]; // Array de UUIDs
    ExternalId: string;
    SignaturesBase64: string[]; // Array de firmas en base64
}

export interface ProtocolDocumentUploadRequest {
    ExternalId: string;
    ProtocolType: number;
    DocumentBase64: string;
    DocumentName: string;
}

export interface UploadProtocolPictureResponse {
    url: string;
    fieldName: string;
}

export interface KanbanClient {
    clientId: number;
    name: string;
    phone: string;
    email: string;
    documentId: string;
}

export interface KanbanVehicle {
    vehicleId: number;
    licensePlate: string;
    brand: string;
    model: string;
    color: string;
    kilometers: number;
    vin: string | null;
    vehicleType?: number;
}

export interface KanbanEmployee {
    employeeId: number;
    name: string;
    profilePictureUrl?: string;
}

export interface KanbanBudgetStatus {
    budgetId: number | null;
    state: BudgetState;
    totalAmount: number | null;
}

export enum BudgetState {
    NoBudget = 0,
    Pending = 1,
    Sent = 2,
    Rejected = 3,
    Accepted = 4
}

export interface KanbanProtocolDetail {
    type: number;
    status: number;
    progress: number;
    startDate: string;
    endDate: string;
}

export interface KanbanAgenda {
    agendaId: number;
    scheduledDateTime: string;
    duration: string;
    status: number;
    serviceName: string;
    jobPositionName?: string;
}

export interface KanbanProtocolOrderItem {
    agendaId: number;
    externalId: string;
    serviceName: string;
    kanbanStatus: number;
    client: KanbanClient;
    vehicle: KanbanVehicle;
    employee: KanbanEmployee;
    entryDate: string;
    estimatedDeliveryDate: string;
    actualDeliveryDate: string;
    entryProtocol: KanbanProtocolDetail;
    workOrder: KanbanProtocolDetail;
    exitProtocol: KanbanProtocolDetail;
    agenda: KanbanAgenda;
    observations: string;
    overallProgress: number;
    requiresPayment: boolean;
    estimatedPrice: number;
    hasInsuranceClaim: boolean;
    insuranceName: string;
    budgetStatus: KanbanBudgetStatus | null;
}

export type WorkshopKanbanResponse = KanbanProtocolOrderItem[];

export interface ProtocolOrderDraft {
    id: string;
    externalId: string;
    vehicleLicensePlate: string;
    startDate: string;
    lastModification: string;
    clientFullName: string | null;
    clientPhone: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class WorkOrdersService {
    private apiUrl = `${environment.cafler_backoffice_back_url}/ProtocolOrder`;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private readonly analytics: AnalyticsService
    ) { }

    getProtocolOrder(id: string): Observable<ProtocolOrderDto> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<ProtocolOrderDto>(`${this.apiUrl}`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    params: { id: id }
                });
            })
        );
    }

    deleteByOrderHash(orderHash: string): Observable<void> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.delete<void>(`${this.apiUrl}/${orderHash}`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    }
                });
            })
        );
    }

    getProtocolByOrderId(orderId: string): Observable<ProtocolOrderFormDto> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                return this.http.get<ProtocolOrderFormDto>(`${this.apiUrl}`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    params: { id: orderId }
                }).pipe(
                    tap((response: any) => {
                    }),
                    catchError((error: any) => {
                        throw error;
                    })
                );
            })
        );
    }

    uploadProtocolDocument(request: ProtocolDocumentUploadRequest): Observable<boolean> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const formData = new FormData();
                formData.append('ExternalId', request.ExternalId);
                formData.append('ProtocolType', request.ProtocolType.toString());
                formData.append('DocumentBase64', request.DocumentBase64);
                formData.append('DocumentName', request.DocumentName);

                return this.http.post<boolean>(`${this.apiUrl}/document-upload`, formData, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                });
            })
        );
    }

    uploadProtocolPicture(file: Blob, externalId: string, protocolType: number, fieldName: string): Observable<UploadProtocolPictureResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const formData = new FormData();
                formData.append('File', file, `${fieldName}.jpg`);
                formData.append('ExternalId', externalId);
                formData.append('ProtocolType', protocolType.toString());
                formData.append('FieldName', fieldName);

                return this.http.post<UploadProtocolPictureResponse>(`${this.apiUrl}/picture-upload`, formData, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                });
            })
        );
    }

    generateExternalId(): Observable<string> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                return this.http.get(`${this.apiUrl}/generate-external-id`, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    responseType: 'text'
                });
            })
        );
    }

    getProtocolPdf(protocolType: number, externalId: string): Observable<string[]> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    return throwError(() => new Error('No token available'));
                }

                const params = new HttpParams()
                    .set('ProtocolType', protocolType.toString())
                    .set('ExternalId', externalId);

                const headers = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });

                return this.http.get<string[]>(`${this.apiUrl}/document-report`, { headers, params });
            })
        );
    }

    getAgendaDetails(id: number): Observable<AgendaDetailsResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<AgendaDetailsResponse>(`${environment.cafler_backoffice_back_url}/Agenda/${id}/details`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            })
        );
    }

    getProtocolOrderDetailsByHash(externalId: string): Observable<ProtocolOrderDetailsResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<ProtocolOrderDetailsResponse>(
                    `${this.apiUrl}/by-order-hash/${externalId}`,
                    {
                        headers: {
                            Authorization: 'Bearer ' + token
                        }
                    }
                );
            })
        );
    }

    getProtocolOrderDetails(id: string): Observable<ProtocolOrderResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<ProtocolOrderResponse>(`${this.apiUrl}`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    },
                    params: { id: id }
                });
            })
        );
    }

    saveProtocolOrderDto(
        protocolDto: ProtocolOrderFormDto,
        protocolType?: ProtocolType,
        newOrderType?: NewOrderType
    ): Observable<ProtocolOrderFormDto> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                let url = `${this.apiUrl}/save-protocol-order`;

                if (protocolType !== undefined) {
                    const queryParam = PROTOCOL_TYPE_TO_QUERY_PARAM[protocolType];
                    if (queryParam) {
                        url += `?type=${queryParam}`;
                    }
                }

                return this.http.put<ProtocolOrderFormDto>(url, protocolDto, {
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                });
            }),
        );
    }

    /**
     * Obtiene información del centro actual
     * Endpoint: GET /api/centers/me
     */
    getCenterInfo(): Observable<CenterInfo> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<CenterInfo>(`${environment.cafler_backoffice_back_url}/centers/me`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            })
        );
    }

    /**
     * Obtiene la información del usuario actual incluyendo el logo del centro
     */
    getCurrentUser(): Observable<CurrentUserResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<CurrentUserResponse>(`${environment.cafler_backoffice_back_url}/users/current`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            })
        );
    }

    getWorkshopKanban(): Observable<WorkshopKanbanResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<WorkshopKanbanResponse>(`${this.apiUrl}/workshop-kanban`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    getDraftProtocolOrders(): Observable<ProtocolOrderDraft[]> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                return this.http.get<ProtocolOrderDraft[]>(`${this.apiUrl}/drafts`, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            }),
            map((drafts) => drafts.map(draft => ({
                ...draft,
                clientFullName: draft.clientFullName ?? (draft as { clientName?: string | null }).clientName ?? null
            }))),
            catchError((error) => {
                console.error('Error obteniendo borradores:', error);
                return throwError(() => error);
            })
        );
    }

    updateKanbanStatus(agendaId: number, kanbanStatus: number): Observable<any> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const url = `${this.apiUrl}/${agendaId}/kanban-status`;
                const body = { kanbanStatus };

                return this.http.put(url, body, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            }),
            tap((response) => {
            }),
            catchError((error) => {
                console.error('Error actualizando kanban status:', error);
                console.error('Error details:', {
                    status: error.status,
                    message: error.message,
                    error: error.error
                });
                return throwError(() => error);
            })
        );
    }

    assignEmployeeToProtocolOrder(protocolOrderId: string, employeeId: number): Observable<any> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const url = `${this.apiUrl}/${protocolOrderId}/assign-employee/${employeeId}`;

                return this.http.put(url, null, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            }),
            catchError((error) => {
                console.error('Error asignando empleado:', error);
                return throwError(() => error);
            })
        );
    }

    removeEmployeeFromProtocolOrder(protocolOrderId: string, employeeId: number): Observable<any> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const url = `${this.apiUrl}/${protocolOrderId}/assign-employee`;

                return this.http.delete(url, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    }
                });
            }),
            catchError((error) => {
                console.error('Error desasignando empleado:', error);
                return throwError(() => error);
            })
        );
    }

    smartAssignBulk(request: SmartAssignProtocolBulkRequest, locale: string = 'es'): Observable<SmartAssignBulkResponse> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }
                const url = `${this.apiUrl}/smart-assign/bulk`;
                return this.http.post<SmartAssignBulkResponse>(url, request, {
                    headers: {
                        Authorization: 'Bearer ' + token
                    },
                    params: { locale }
                });
            }),
            catchError((error) => {
                console.error('Error in bulk smart assign:', error);
                return throwError(() => error);
            })
        );
    }
}
